import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { roleStore } from "@/store/user/role-store";
import { useGetAllRoles } from "@/hooks/user/use-role";
import { groupStore } from "@/store/group/group-store";
import { useGetAllGroups } from "@/hooks/user/use-group";
import ProcessTable from "@/components/process/process-table";
import { useGetProcessFolderById } from "@/hooks/process-folder/use-process-folder";
import { useBreadcrumbStore } from "@/store/ui";
import { Icon } from "@iconify/react";

const ProcessesInFolder = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const { t } = useTranslation();
  const { data: groups } = useGetAllGroups();
  const { data: roles } = useGetAllRoles();
  const { setGroups } = groupStore();
  const { setRoles } = roleStore();
  const { setCustomBreadcrumbItems } = useBreadcrumbStore();

  const { data: folderData, isPending: isFolderPending } =
    useGetProcessFolderById(folderId || "");

  useEffect(() => {
    if (groups) setGroups(groups);
    if (roles) setRoles(roles);
  }, [groups, roles, setGroups, setRoles]);

  // Set custom breadcrumbs with folder name
  useEffect(() => {
    if (folderData) {
      setCustomBreadcrumbItems([
        { name: t("sidebar.home"), href: "/" },
        { name: t("sidebar.processNames"), href: "/admin/processes" },
        { name: folderData.name, href: "" },
      ]);
    } else {
      setCustomBreadcrumbItems(null);
    }
  }, [folderData, t, setCustomBreadcrumbItems]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setCustomBreadcrumbItems(null);
    };
  }, [setCustomBreadcrumbItems]);

  // Use processes directly from folder response
  const folderProcesses = folderData?.processes || [];

  return (
    <main className="">
      <div className="text-2xl font-semibold text-center py-8">
        {isFolderPending ? (
          <div className="flex items-center justify-center gap-2">
            <Icon icon="eos-icons:loading" className="text-xl" />
            <span>{t("processManagement.loading")}</span>
          </div>
        ) : (
          <p>{folderData?.name || t("processManagement.processes")}</p>
        )}
      </div>
      <div className="space-y-6">
        {isFolderPending ? (
          <p className="h-full flex justify-center items-center">
            {t("processManagement.fetching")}
          </p>
        ) : (
          <ProcessTable processes={folderProcesses} folderId={folderId} />
        )}
      </div>
    </main>
  );
};

export default ProcessesInFolder;
